import { Box, Container, LinearProgress } from '@material-ui/core'
import { ContainerPage, Summary, TitlePage } from 'components'
import {
  CardBreak,
  CardInfo,
  CardInfoToggle,
  CardRoute,
  ModalBreak,
  SeparatorList,
  ViewToggle,
  DashboardAlerts,
} from 'components/pages/Dashboard'
import { ModalRoute } from 'components/pages/Dashboard/ModalRoute'
import { useBreak } from 'hooks/useBreak'
import { useConferenceChuchu } from 'hooks/useConferenceChuchu'
import { useDashboard } from 'hooks/useDashboard'
import { IRouteDashboard } from 'interfaces/IDashboard'
import { useState, useEffect, useRef } from 'react'

import { useStyles } from './styles'

const Dashboard = () => {
  const classes = useStyles()
  const [modalBreak, setModalBreak] = useState(false)
  const { dashboardData, refreshDashboard, loading } = useDashboard()
  const { recoveryConference } = useConferenceChuchu()
  const { breaks } = useBreak()
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0)
  const [viewMode, setViewMode] = useState<'orders' | 'items'>('orders')
  const boxRef = useRef<HTMLDivElement>(null)
  const [openModalRoute, setOpenModalRoute] = useState({
    open: false,
    routeData: {} as IRouteDashboard,
  })
  const {
    data,
    completed_orders,
    total_orders,
    total_volume,
    total_ifood_orders,
    total_frexco_orders,
    processed_volume,
    updated_in,
    orders_in_progress,
    average_idle_time,
    tons_per_hour,
    orders_without_route,
    orders_pending_status,
  } = dashboardData

  const pendentOrders = total_orders - orders_in_progress - completed_orders

  const toggleModalBreak = () => {
    setModalBreak(state => !state)
  }

  const handleClose = () => {
    setOpenModalRoute(state => ({ ...state, open: false }))
  }
  const onOpenModal = (routeData: IRouteDashboard) => {
    setOpenModalRoute(state => ({ ...state, open: true, routeData }))
  }
  const onRouteData = (routeData: IRouteDashboard) => {
    setOpenModalRoute(state => ({ ...state, routeData }))
  }

  const handleTimeCardClick = () => {
    setCurrentTimeIndex(prevIndex => (prevIndex + 1) % 4)
  }

  const handleViewToggle = () => {
    setViewMode(prev => (prev === 'orders' ? 'items' : 'orders'))
  }

  const getTimeCardData = () => {
    if (viewMode === 'items') {
      // Visualização por itens - mostra apenas tempo médio por item
      return {
        title: 'Tempo médio por item',
        timeData: dashboardData.average_time_per_item,
        isLiquidTime: false,
        iconType: 'cycle' as const,
        showAsSeconds: true,
        helpText: undefined,
      }
    }

    // Visualização por pedidos - carrossel normal
    const timeConfigs = [
      {
        title: 'Tempo médio / pedido (com pausas)',
        timeData: Number(dashboardData.average_cycle_time),
        isLiquidTime: false,
        iconType: 'cycle' as const,
        showAsSeconds: false,
        helpText:
          'Tempo médio + Pausas. Calculo: ((segundos_total + ociosidade_total) / qtd_pedidos) / 60',
      },
      {
        title: 'Tempo médio / pedido',
        timeData: Number(dashboardData.average_duration),
        isLiquidTime: false,
        iconType: 'gross' as const,
        showAsSeconds: false,
        helpText: 'Apenas o tempo médio, sem pausas. Calculo: (segundos_total / qtd_pedidos) / 60',
      },
      {
        title: 'Tempo médio líquido / pedido',
        timeData: Number(dashboardData.avg_time_first_to_last_item.average_time_seconds),
        isLiquidTime: true,
        iconType: 'liquid' as const,
        showAsSeconds: false,
        helpText:
          'Tempo médio entre o primeiro e ultimo item bipado. Não considera etiquetagem, deslocamento caixas, etc.',
      },
      {
        title: 'Tempo médio ocioso / pedido',
        timeData: dashboardData.average_idle_time,
        isLiquidTime: false,
        iconType: 'idle' as const,
        showAsSeconds: false,
        helpText: undefined,
      },
    ]

    return timeConfigs[currentTimeIndex]
  }

  const AverageTime = () => {
    const currentTimeData = getTimeCardData()

    return (
      <div ref={boxRef}>
        <Box onClick={handleTimeCardClick} style={{ cursor: 'pointer' }}>
          <CardInfo
            title={currentTimeData.title}
            isLiquidTime={currentTimeData.isLiquidTime}
            timeData={currentTimeData.timeData}
            iconType={currentTimeData.iconType}
            showAsSeconds={currentTimeData.showAsSeconds}
            helpText={currentTimeData.helpText}
          />
        </Box>
      </div>
    )
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        // Removido o reset do índice para manter o carrossel funcionando
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [boxRef])

  useEffect(() => {
    recoveryConference()
  }, [])

  return (
    <>
      <ContainerPage>
        <Box className={classes.headerContainer}>
          <TitlePage>Dashboard</TitlePage>
          <ViewToggle viewMode={viewMode} onToggle={handleViewToggle} />
        </Box>
        <DashboardAlerts
          ordersWithoutRoute={orders_without_route}
          ordersPendingStatus={orders_pending_status}
        />
        <Box className={classes.containerInfos}>
          <CardInfoToggle
            title={
              viewMode === 'orders'
                ? 'Previsão de término / pedidos'
                : 'Previsão de término / itens'
            }
            totalOrder={
              viewMode === 'orders'
                ? dashboardData.total_orders - dashboardData.completed_orders
                : dashboardData.items_remaining
            }
            endTime={
              viewMode === 'orders' ? dashboardData.end_time : dashboardData.end_time_by_items
            }
            numberSeparators={dashboardData.separators_quantity_config}
            separatorsQuantityToday={dashboardData.separators_quantity_today}
            viewMode={viewMode}
          />

          <AverageTime />

          <CardBreak quantity={breaks.length} action={toggleModalBreak} />

          <CardRoute onOpenModal={onOpenModal} onRouteData={onRouteData} />
        </Box>
        <Container className={classes.content}>
          <Box className={classes.summaryContainer}>
            <Summary
              totalItens={dashboardData?.total_items}
              totalOrders={total_orders}
              totalFrexcoOrders={total_frexco_orders || { finalized: 0, total: 0 }}
              totalIfoodOrders={total_ifood_orders || { finalized: 0, total: 0 }}
              completed={completed_orders}
              separationOrders={orders_in_progress}
              processedVolume={processed_volume}
              totalVolume={total_volume}
              lastUpdate={updated_in}
              pendentOrders={pendentOrders}
              tonsPerHour={tons_per_hour}
              viewMode={viewMode}
              processedItems={dashboardData.processed_items}
              itemsInProgress={dashboardData.items_in_progress}
              itemsRemaining={dashboardData.items_remaining}
              itemsCompletionPercentage={dashboardData.items_completion_percentage}
              ordersCompletionPercentage={dashboardData.orders_completion_percentage}
              historicalComparison={dashboardData.historical_comparison}
            />
          </Box>

          <Box className={classes.groupsContainer}>
            <LinearProgress
              style={{ height: '10px', borderRadius: '4px', opacity: loading ? 1 : 0 }}
            />
            <SeparatorList
              separators={data}
              averageDuration={dashboardData.average_duration}
              pendentOrders={pendentOrders}
              refreshDashboard={refreshDashboard}
              averageIdleTime={average_idle_time}
            />
          </Box>
        </Container>
      </ContainerPage>

      <ModalBreak open={modalBreak} handleClose={toggleModalBreak} breaks={breaks} />
      <ModalRoute
        data={openModalRoute.routeData}
        handleClose={handleClose}
        open={openModalRoute.open}
      />
    </>
  )
}

export default Dashboard
